import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Badge } from '../../../components/ui/Badge';
import { Input } from '../../../components/ui/Input';
import { Modal } from '../../../components/ui/Modal';
import {
  BarChart3, TrendingUp, DollarSign, Download, Calendar, Filter, RefreshCw, Eye, FileText, PieChart, LineChart, Users, CreditCard, Receipt, AlertCircle
} from 'lucide-react';
import MockDataService from '../../../services/MockDataService';

const BillingReports = () => {
  const [reports, setReports] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedReport, setSelectedReport] = useState(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [filters, setFilters] = useState({
    dateRange: '30d',
    reportType: 'all',
    status: 'all'
  });
  const [generatingReport, setGeneratingReport] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [reportsResponse, analyticsResponse] = await Promise.all([
          MockDataService.getBillingReports(),
          MockDataService.getBillingAnalytics()
        ]);

        if (reportsResponse.success) {
          setReports(reportsResponse.data);
        }
        if (analyticsResponse.success) {
          setAnalytics(analyticsResponse.data);
        }
      } catch (err) {
        setError("Failed to fetch billing reports data.");
        console.error("Error fetching billing reports:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleGenerateReport = async (reportType) => {
    try {
      setGeneratingReport(true);
      const response = await MockDataService.generateBillingReport(reportType, filters);
      if (response.success) {
        setReports(prev => [response.data, ...prev]);
        alert(`${reportType} report generated successfully!`);
      } else {
        alert('Failed to generate report');
      }
    } catch (err) {
      alert('Failed to generate report');
      console.error("Error generating report:", err);
    } finally {
      setGeneratingReport(false);
    }
  };

  const handleExportReport = async (reportId) => {
    try {
      const response = await MockDataService.exportBillingReport(reportId);
      if (response.success) {
        alert('Report exported successfully!');
      } else {
        alert('Failed to export report');
      }
    } catch (err) {
      alert('Failed to export report');
      console.error("Error exporting report:", err);
    }
  };

  const handleViewReport = (report) => {
    setSelectedReport(report);
    setShowReportModal(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'success';
      case 'generating': return 'secondary';
      case 'failed': return 'destructive';
      case 'pending': return 'outline';
      default: return 'outline';
    }
  };

  const getReportTypeIcon = (type) => {
    switch (type) {
      case 'revenue': return <DollarSign className="h-4 w-4" />;
      case 'subscription': return <CreditCard className="h-4 w-4" />;
      case 'invoice': return <Receipt className="h-4 w-4" />;
      case 'customer': return <Users className="h-4 w-4" />;
      default: return <FileText className="h-4 w-4" />;
    }
  };

  const filteredReports = reports.filter(report => {
    const matchesType = filters.reportType === 'all' || report.type === filters.reportType;
    const matchesStatus = filters.status === 'all' || report.status === filters.status;
    return matchesType && matchesStatus;
  });

  if (loading) return <div className="p-6 text-center">Loading Billing Reports...</div>;
  if (error) return <div className="p-6 text-center text-red-500">Error: {error}</div>;

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Billing Reports</h1>
          <p className="text-gray-600 dark:text-gray-400">Generate and manage billing reports and analytics.</p>
        </div>
        <div className="flex space-x-2">
          <Button variant="outline" onClick={() => handleGenerateReport('revenue')} disabled={generatingReport}>
            <BarChart3 className={`h-4 w-4 mr-2 ${generatingReport ? 'animate-spin' : ''}`} />
            Generate Revenue Report
          </Button>
          <Button variant="outline" onClick={() => handleGenerateReport('subscription')} disabled={generatingReport}>
            <CreditCard className={`h-4 w-4 mr-2 ${generatingReport ? 'animate-spin' : ''}`} />
            Generate Subscription Report
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Report Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4">
            <div className="flex items-center space-x-2">
              <Calendar className="h-4 w-4 text-gray-500" />
              <select
                value={filters.dateRange}
                onChange={(e) => setFilters({...filters, dateRange: e.target.value})}
                className="px-3 py-2 border rounded-md"
              >
                <option value="7d">Last 7 Days</option>
                <option value="30d">Last 30 Days</option>
                <option value="90d">Last 90 Days</option>
                <option value="1y">Last Year</option>
                <option value="custom">Custom Range</option>
              </select>
            </div>

            <select
              value={filters.reportType}
              onChange={(e) => setFilters({...filters, reportType: e.target.value})}
              className="px-3 py-2 border rounded-md"
            >
              <option value="all">All Report Types</option>
              <option value="revenue">Revenue Reports</option>
              <option value="subscription">Subscription Reports</option>
              <option value="invoice">Invoice Reports</option>
              <option value="customer">Customer Reports</option>
            </select>

            <select
              value={filters.status}
              onChange={(e) => setFilters({...filters, status: e.target.value})}
              className="px-3 py-2 border rounded-md"
            >
              <option value="all">All Statuses</option>
              <option value="completed">Completed</option>
              <option value="generating">Generating</option>
              <option value="failed">Failed</option>
              <option value="pending">Pending</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Analytics Overview */}
      {analytics && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
              <DollarSign className="h-4 w-4 text-gray-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">${analytics.totalRevenue.toLocaleString()}</div>
              <p className="text-xs text-gray-500">
                <span className="text-green-600">+{analytics.revenueGrowth}%</span> from last month
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Active Subscriptions</CardTitle>
              <CreditCard className="h-4 w-4 text-gray-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{analytics.activeSubscriptions}</div>
              <p className="text-xs text-gray-500">
                <span className="text-green-600">+{analytics.subscriptionGrowth}</span> this month
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Monthly Recurring Revenue</CardTitle>
              <TrendingUp className="h-4 w-4 text-gray-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">${analytics.mrr.toLocaleString()}</div>
              <p className="text-xs text-gray-500">
                <span className="text-green-600">+{analytics.mrrGrowth}%</span> growth rate
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Churn Rate</CardTitle>
              <AlertCircle className="h-4 w-4 text-gray-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{analytics.churnRate}%</div>
              <p className="text-xs text-gray-500">
                <span className="text-red-600">{analytics.churnRate > 5 ? 'High' : 'Low'}</span> churn rate
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Revenue Trends */}
      {analytics && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Revenue Trends</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64 flex items-center justify-center text-gray-500 dark:text-gray-400">
                <LineChart className="h-12 w-12" />
                <span className="ml-2">Revenue chart coming soon...</span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Subscription Distribution</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64 flex items-center justify-center text-gray-500 dark:text-gray-400">
                <PieChart className="h-12 w-12" />
                <span className="ml-2">Subscription chart coming soon...</span>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Reports List */}
      <Card>
        <CardHeader>
          <CardTitle>Generated Reports ({filteredReports.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {filteredReports.map((report) => (
              <div key={report.id} className="border rounded-lg p-4">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      {getReportTypeIcon(report.type)}
                      <h3 className="text-lg font-semibold">{report.name}</h3>
                      <Badge variant={getStatusColor(report.status)}>
                        {report.status}
                      </Badge>
                    </div>
                    <p className="text-gray-600 dark:text-gray-400 mb-2">{report.description}</p>
                    <div className="flex items-center space-x-4 text-sm text-gray-500">
                      <span className="flex items-center">
                        <Calendar className="h-4 w-4 mr-1" />
                        {new Date(report.createdAt).toLocaleDateString()}
                      </span>
                      <span className="flex items-center">
                        <FileText className="h-4 w-4 mr-1" />
                        {report.format}
                      </span>
                      <span className="flex items-center">
                        <DollarSign className="h-4 w-4 mr-1" />
                        ${report.totalAmount?.toLocaleString() || 'N/A'}
                      </span>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleViewReport(report)}
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      View
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleExportReport(report.id)}
                    >
                      <Download className="h-4 w-4 mr-1" />
                      Export
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Report Details Modal */}
      <Modal isOpen={showReportModal} onClose={() => setShowReportModal(false)} className="max-w-4xl">
        <div className="p-6">
          <h2 className="text-xl font-bold mb-4">Report Details</h2>
          {selectedReport && (
            <div className="space-y-4">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-lg font-semibold">{selectedReport.name}</h3>
                  <p className="text-gray-600">{selectedReport.description}</p>
                </div>
                <Badge variant={getStatusColor(selectedReport.status)}>
                  {selectedReport.status}
                </Badge>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <strong>Type:</strong> {selectedReport.type}
                </div>
                <div>
                  <strong>Format:</strong> {selectedReport.format}
                </div>
                <div>
                  <strong>Generated:</strong> {new Date(selectedReport.createdAt).toLocaleString()}
                </div>
                <div>
                  <strong>Period:</strong> {selectedReport.period}
                </div>
                <div>
                  <strong>Total Amount:</strong> ${selectedReport.totalAmount?.toLocaleString() || 'N/A'}
                </div>
                <div>
                  <strong>Record Count:</strong> {selectedReport.recordCount}
                </div>
              </div>

              {selectedReport.summary && (
                <div>
                  <h4 className="font-medium mb-2">Summary:</h4>
                  <div className="bg-gray-50 p-4 rounded-md">
                    <pre className="text-sm whitespace-pre-wrap">{selectedReport.summary}</pre>
                  </div>
                </div>
              )}

              {selectedReport.data && (
                <div>
                  <h4 className="font-medium mb-2">Report Data:</h4>
                  <div className="bg-gray-50 p-4 rounded-md max-h-64 overflow-auto">
                    <pre className="text-xs">{JSON.stringify(selectedReport.data, null, 2)}</pre>
                  </div>
                </div>
              )}

              <div className="flex justify-end space-x-2">
                <Button variant="outline" onClick={() => setShowReportModal(false)}>
                  Close
                </Button>
                <Button onClick={() => handleExportReport(selectedReport.id)}>
                  <Download className="h-4 w-4 mr-2" />
                  Export Report
                </Button>
              </div>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default BillingReports;
